import React, { useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useAuth } from '../../Context/AuthContext';
import { toast } from 'react-toastify';
import { getAllProviderSettings } from '../../Services/ProviderService';
import SearchableDropdown from '../Subscription/SearchableDropdown';
import DocumentUpload from '../Common/DocumentUpload';
import CustomDatePicker from '../Common/CustomDatePicker';

const TEST_TYPE_SETTING_ID = 40;

const mapProviderSettingItem = (item) => ({
  value: Number(item.id ?? item.Id ?? item.value ?? item.value_id ?? ''),
  label: item.name ?? item.Name ?? item.code ?? String(item.id ?? ''),
});

const STATUS_OPTIONS = [
  { id: 1, label: 'Active' },
  { id: 2, label: 'Inactive' },
];

const emptyForm = {
  id: null,
  provider_id: null,
  status_id: null,
  file_id: null,
  test_type: '',
  completion_date: null,
  expiration_date: null,
  type: 'create',
  document: null,
};

const validationSchema = Yup.object({
  status_id: Yup.number().nullable(),
  test_type: Yup.number().nullable(),
  completion_date: Yup.date().nullable().typeError('Invalid date format'),
  expiration_date: Yup.date().nullable().typeError('Invalid date format'),
  document: Yup.mixed().nullable(),
  type: Yup.string().trim().nullable(),
});

export default function HealthInfoModal({ isOpen, providerId, onClose, onSave, initialData, isLoading }) {
  const { token } = useAuth();
  const [testTypeOptions, setTestTypeOptions] = useState([]);
  const [settingsLoading, setSettingsLoading] = useState(false);

  const initialValues = useMemo(() => {
    const base = initialData ? { ...emptyForm, ...initialData } : { ...emptyForm, provider_id: providerId };

    return {
      ...base,
      provider_id: base.provider_id || providerId || null,
      document: initialData?.file_name || null,
      type: initialData?.type || (initialData?.id ? 'update' : 'create'),
    };
  }, [initialData, providerId]);

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values) => {
      const payload = {
        ...values,
        id: values.id || null,
        test_type: values.test_type !== '' && values.test_type !== null && values.test_type !== undefined ? Number(values.test_type) : null,
        status_id: values.status_id !== '' && values.status_id !== null && values.status_id !== undefined ? Number(values.status_id) : null,
        completion_date: values.completion_date || null,
        expiration_date: values.expiration_date || null,
        type: values.id ? 'update' : 'create',
      };
      onSave(payload);
    },
  });

  const loadTestTypeOptions = async () => {
    if (!token) return;
    setSettingsLoading(true);
    try {
      const response = await getAllProviderSettings(token, TEST_TYPE_SETTING_ID);
      if (response?.is_success && Array.isArray(response.data)) {
        setTestTypeOptions(response.data.map(mapProviderSettingItem));
      } else if (response?.is_success === false && response?.status_code === 500) {
        toast.error('Internal Server Error. Please Contact Support Team.');
        setTestTypeOptions([]);
      } else {
        toast.error(response?.message || 'Failed to load test type settings');
        setTestTypeOptions([]);
      }
    } catch (error) {
      console.error('Failed to load test type settings:', error);
      setTestTypeOptions([]);
    } finally {
      setSettingsLoading(false);
    }
  };

  useEffect(() => {
    loadTestTypeOptions();
  }, [token]);

  if (!isOpen) return null;

  return (
    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(var(--hc-black-rgb), 0.5)' }}>
      <div className="modal-dialog modal-xl modal-dialog-centered">
        <div className="modal-content border-0 rounded-4">
          <div className="modal-header border-bottom-0 px-4 py-2 d-flex justify-content-between align-items-center">
            <h5 className="modal-title fw-bold hc-text-primary m-0">
              {initialData?.id ? 'Edit Health Info' : 'Add Health Info'}
            </h5>
            <button type="button" className="btn-close" onClick={onClose} disabled={isLoading} />
          </div>

          <form onSubmit={formik.handleSubmit}>
            <input type="hidden" name="provider_id" value={formik.values.provider_id || ''} />
            <input type="hidden" name="status_id" value={formik.values.status_id || ''} />
            <input type="hidden" name="file_id" value={formik.values.file_id || ''} />
            <input type="hidden" name="type" value={formik.values.type || ''} />

            <div className="modal-body px-4 py-2">
              <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
                <h6 className="fw-semibold hc-text-primary mb-3">Health Information</h6>
                <div className="row g-3">
                  <div className="col-md-6">
                    <SearchableDropdown
                      label="Test Type"
                      placeholder="Select Option"
                      options={testTypeOptions}
                      value={formik.values.test_type}
                      onChange={(value) => formik.setFieldValue('test_type', value)}
                      disabled={settingsLoading}
                      loading={settingsLoading}
                      emptyMessage="No test types found"
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Status</label>
                    <select
                      {...formik.getFieldProps('status_id')}
                      className="form-select rounded-3 shadow-sm"
                    >
                      <option value="">Select Option</option>
                      {STATUS_OPTIONS.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Completion Date</label>
                    <CustomDatePicker
                        {...formik.getFieldProps('completion_date')}
                        formik={formik}
                      />
                    {/* <input
                      type="date"
                      {...formik.getFieldProps('completion_date')}
                      className="form-control rounded-3 shadow-sm"
                    /> */}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Expiration Date</label>
                      <CustomDatePicker
                        {...formik.getFieldProps('expiration_date')}
                        formik={formik}
                      />
                  </div>
                </div>
              </div>

              <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
                <h6 className="fw-semibold hc-text-primary mb-3">Document</h6>
                <DocumentUpload
                  id="health-info-document-input"
                  label="Upload Document"
                  value={formik.values.document}
                  onChange={(file) => formik.setFieldValue('document', file)}
                />
              </div>
            </div>

            <div className="modal-footer border-top-0 p-4 d-flex justify-content-end gap-2">
              <button type="button" className="btn btn-light rounded-3 px-4" onClick={onClose} disabled={isLoading}>
                Cancel
              </button>
              <button type="submit" className="btn hc-bg-primary text-white rounded-3 px-4" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                    Saving...
                  </>
                ) : (
                  'Save'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
